/* eslint-disable @typescript-eslint/indent */
/* eslint-disable complexity */
/* eslint-disable max-statements */
/* eslint-disable max-lines */
import { useMemo, useCallback, useContext } from 'react';
import { ControlledDateTimePickerContext } from '../../../ControlledDateTimePickerCTX.js';
import type { DSControlledDateTimePickerInternalsT } from '../../../sharedTypes.js';
import type { useCurrentDisplayedWheelsLogic } from './useCurrentDisplayedWheelsLogic.js';
interface UseKeyboardHandlersArgs {
  currentDisplayedTimeWheelLogic: ReturnType<typeof useCurrentDisplayedWheelsLogic>;
}
interface KeyboardHandlersT {
  handleCurrHourOnKeyDown: (e: React.KeyboardEvent) => void;
  handleCurrMinutesOnKeyDown: (e: React.KeyboardEvent) => void;
  handleCurrMeridiemOnKeyDown: (e: React.KeyboardEvent) => void;
  handlePickerIconKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
}
export const useKeyboardHandlers = ({ currentDisplayedTimeWheelLogic }: UseKeyboardHandlersArgs): KeyboardHandlersT => {
  const { handlePrevHour, handleNextHour, handlePrevMinute, handleNextMinute, handleTimeWheelBtnChangeMeridiem } =
    currentDisplayedTimeWheelLogic;
  const {
    withAnyInputs,
    isWithTimeInputs,
    isWithDateInputs,
    isWithCalendarToo,
    isControllerOnly,
    props: { readOnly, applyAriaDisabled, disabled },

    trackFocusCalendarPrevMonth,
    trackFocusTimewheelCurrHour,
    trackFocusTimewheelCurrMinute,
    trackFocusTimewheelCurrMeridiem,
    trackFocusFirstSegment,
    trackFocusMeridiemInput,
    trackFocusYearInput,
    trackFocusCalendarMetafocusedDay,
  } = useContext(ControlledDateTimePickerContext);

  // const tryToFocusDayRegion = useCallback(() => {
  //   if (!focusedDay) {
  //     const newFocusDay = daysArray.find(({ isCurrMonthDay }) => isCurrMonthDay);
  //     if (newFocusDay) handleFocusMetaDay(newFocusDay);
  //   }
  //   trackFocusCalendarMetafocusedDay();
  // }, [daysArray, focusedDay, handleFocusMetaDay, trackFocusCalendarMetafocusedDay]);

  const onHomeKeyDownInsideTimeWheel = useCallback(() => {
    if (isWithCalendarToo) trackFocusCalendarPrevMonth();
    else trackFocusTimewheelCurrHour();
  }, [isWithCalendarToo, trackFocusCalendarPrevMonth, trackFocusTimewheelCurrHour]);
  const onEndKeyDownInsideTimeWheel = useCallback(() => {
    trackFocusTimewheelCurrMeridiem();
  }, [trackFocusTimewheelCurrMeridiem]);

  const onHomeKeyDownTriggerIcon = useCallback(() => {
    trackFocusFirstSegment();
  }, [trackFocusFirstSegment]);
  const handlePickerIconKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown<HTMLButtonElement> = useCallback(
    (e: React.KeyboardEvent) => {
      const { key } = e;
      if (['Home', 'End'].includes(key)) e.preventDefault();
      if (['Home', 'End'].includes(key)) e.stopPropagation();
      if (key === 'Home') onHomeKeyDownTriggerIcon();
      if (key === 'Backspace' && withAnyInputs) {
        if (isWithTimeInputs) trackFocusMeridiemInput();
        else if (isWithDateInputs) trackFocusYearInput();
      }
    },
    [
      isWithDateInputs,
      isWithTimeInputs,
      onHomeKeyDownTriggerIcon,
      trackFocusMeridiemInput,
      trackFocusYearInput,
      withAnyInputs,
    ],
  );

  const handleCurrHourOnKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const { key, shiftKey } = e;
      if (['Home', 'End', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) e.preventDefault();
      if (['Home', 'End', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) e.stopPropagation();
      let didTriggerHourChange = false;
      if (key === 'ArrowUp') {
        if (readOnly || applyAriaDisabled || disabled) return;
        handlePrevHour(e);
        didTriggerHourChange = true;
      }
      if (key === 'ArrowDown') {
        if (readOnly || applyAriaDisabled || disabled) return;
        handleNextHour(e);
        didTriggerHourChange = true;
      }
      if (key === 'ArrowRight') {
        trackFocusTimewheelCurrMinute();
      }
      if (key === 'Home') onHomeKeyDownInsideTimeWheel();
      if (key === 'End') onEndKeyDownInsideTimeWheel();

      // ensure the focus is in the correct button after up/down arrow
      if (didTriggerHourChange) trackFocusTimewheelCurrHour();

      // TAB CYCLE IMPLEMENTATION
      if (!isControllerOnly)
        if (key === 'Tab' && shiftKey && !isWithCalendarToo) {
          e.preventDefault();
          trackFocusTimewheelCurrMeridiem();
        }

      if (key === 'Tab' && shiftKey && isWithCalendarToo) {
        trackFocusCalendarMetafocusedDay();
      }
    },
    [
      onHomeKeyDownInsideTimeWheel,
      onEndKeyDownInsideTimeWheel,
      trackFocusTimewheelCurrHour,
      isControllerOnly,
      isWithCalendarToo,
      readOnly,
      applyAriaDisabled,
      disabled,
      handlePrevHour,
      handleNextHour,
      trackFocusTimewheelCurrMinute,
      trackFocusTimewheelCurrMeridiem,
      trackFocusCalendarMetafocusedDay,
    ],
  );
  const handleCurrMinutesOnKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const { key } = e;
      if (['Home', 'End', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) e.preventDefault();
      if (['Home', 'End', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) e.stopPropagation();
      let didTriggerMinuteChange = false;
      if (key === 'ArrowUp') {
        if (readOnly || applyAriaDisabled || disabled) return;
        handlePrevMinute(e);
        didTriggerMinuteChange = true;
      }
      if (key === 'ArrowDown') {
        if (readOnly || applyAriaDisabled || disabled) return;
        handleNextMinute(e);
        didTriggerMinuteChange = true;
      }
      if (key === 'ArrowLeft') {
        trackFocusTimewheelCurrHour();
      }
      if (key === 'ArrowRight') {
        trackFocusTimewheelCurrMeridiem();
      }
      if (key === 'Home') onHomeKeyDownInsideTimeWheel();
      if (key === 'End') onEndKeyDownInsideTimeWheel();
      // ensure the focus is in the correct button after up/down arrow
      if (didTriggerMinuteChange) trackFocusTimewheelCurrMinute();
    },
    [
      onHomeKeyDownInsideTimeWheel,
      onEndKeyDownInsideTimeWheel,
      trackFocusTimewheelCurrMinute,
      readOnly,
      applyAriaDisabled,
      disabled,
      handlePrevMinute,
      handleNextMinute,
      trackFocusTimewheelCurrHour,
      trackFocusTimewheelCurrMeridiem,
    ],
  );
  const handleCurrMeridiemOnKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const { key, shiftKey } = e;
      if (['Home', 'End', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) e.preventDefault();
      if (['Home', 'End', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) e.stopPropagation();
      let didTriggerMeridiemChange = false;
      if (key === 'ArrowUp') {
        if (readOnly || applyAriaDisabled || disabled) return;
        handleTimeWheelBtnChangeMeridiem('AM', e);
        didTriggerMeridiemChange = true;
      }
      if (key === 'ArrowDown') {
        if (readOnly || applyAriaDisabled || disabled) return;
        handleTimeWheelBtnChangeMeridiem('PM', e);
        didTriggerMeridiemChange = true;
      }
      if (key === 'ArrowLeft') {
        trackFocusTimewheelCurrMinute();
      }
      if (key === 'Home') onHomeKeyDownInsideTimeWheel();
      if (key === 'End') onEndKeyDownInsideTimeWheel();

      // ensure the focus is in the correct button after up/down arrow
      if (didTriggerMeridiemChange) trackFocusTimewheelCurrMeridiem();

      // TAB CYCLE IMPLEMENTATION
      if (!isControllerOnly)
        if (key === 'Tab' && !shiftKey) {
          e.preventDefault();
          if (isWithCalendarToo) trackFocusCalendarPrevMonth();
          else trackFocusTimewheelCurrHour();
        }
    },
    [
      onHomeKeyDownInsideTimeWheel,
      onEndKeyDownInsideTimeWheel,
      trackFocusTimewheelCurrMeridiem,
      isControllerOnly,
      readOnly,
      applyAriaDisabled,
      disabled,
      handleTimeWheelBtnChangeMeridiem,
      trackFocusTimewheelCurrMinute,
      isWithCalendarToo,
      trackFocusCalendarPrevMonth,
      trackFocusTimewheelCurrHour,
    ],
  );

  return useMemo(
    () => ({
      handleCurrHourOnKeyDown,
      handleCurrMinutesOnKeyDown,
      handleCurrMeridiemOnKeyDown,
      handlePickerIconKeyDown,
    }),
    [handleCurrHourOnKeyDown, handleCurrMeridiemOnKeyDown, handleCurrMinutesOnKeyDown, handlePickerIconKeyDown],
  );
};
